import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { BsArrowRight, BsShieldLock } from "react-icons/bs";
import {
  IoAlertCircleSharp,
  IoEye,
  IoEyeOff,
  IoMailOutline,
  IoLockClosedOutline,
} from "react-icons/io5";
import { RiGovernmentLine } from "react-icons/ri";
import LoadingComponent from "../Components/LoadingComponent";
import ChangePasswordModal from "../Components/ChangePasswordModal";
import useAuth from "../useAuth";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const { user, authMessage, loading, loginWithEmailAndPassword, changePassword } = useAuth();
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  React.useEffect(() => {
    if (!user) return;
    const pwd = user?.passwordChangedAt;
    // if passwordChangedAt is null/undefined, show change-password modal on login page
    if (pwd === "0001-01-01T00:00:00" ) {
      setIsPasswordModalOpen(true);
    }
  }, [user]);

  const handleLogin = async (e) => {
    e.preventDefault();
    loginWithEmailAndPassword(email, password);
  };

  if (user) {
    const pwd = user?.passwordChangedAt;
    if (pwd === null || pwd === undefined || pwd === "0001-01-01T00:00:00") {
      // keep user on login page but show the change-password modal (handled by state)
    } else {
      if (user?.userType === "admin") return <Navigate to="/my/home" />;
      else return <Navigate to="/my" />;
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center p-4">
      {/* Main Container */}
      <div className="w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Panel - Branding */}
          <div className="bg-gradient-to-br from-blue-900 to-blue-700 p-8 lg:p-12 relative overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 left-0 w-64 h-64 bg-blue-800/30 rounded-full -translate-x-32 -translate-y-32"></div>
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-blue-600/20 rounded-full translate-x-40 translate-y-40"></div>

            <div className="relative z-10 h-full flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-fit h-16 px-2 bg-white rounded-lg flex items-center justify-center shadow-lg">
                    <img
                      src="/assets/logo.png"
                      alt="Logo"
                      className="w-full h-full rounded-lg object-cover"
                    />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white">
                      Panchayati Raj
                    </h1>
                    <p className="text-blue-200 text-sm">Digital Platform</p>
                  </div>
                </div>

                <div className="space-y-6 mt-12">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-800/40 flex items-center justify-center">
                      <BsShieldLock className="text-2xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">
                        Secure Access
                      </h3>
                      <p className="text-blue-200 text-sm">
                        Encrypted & Protected
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-800/40 flex items-center justify-center">
                      <RiGovernmentLine className="text-2xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">
                        Government Portal
                      </h3>
                      <p className="text-blue-200 text-sm">Official Platform</p>
                    </div>
                  </div>
                </div>
              </div>

          
            </div>
          </div>

          {/* Right Panel - Login Form */}
          <div className="p-8 lg:p-12">
            <div className="max-w-md mx-auto">
              {/* Header */}
              <div className="text-center mb-10">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Welcome Back
                </h1>
                <p className="text-gray-600">
                  Sign in to your account to continue
                </p>
              </div>

              {/* Login Form */}
              <form onSubmit={handleLogin} className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Username / Email
                  </label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors">
                      <IoMailOutline size={20} />
                    </div>
                    <input
                      type="text"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your username or email"
                      className="w-full pl-12 pr-4 py-3.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 transition-colors">
                      <IoLockClosedOutline size={20} />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="w-full pl-12 pr-12 py-3.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      required
                    />
                    <div
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute cursor-pointer right-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showPassword ? (
                        <IoEyeOff size={20} />
                      ) : (
                        <IoEye size={20} />
                      )}
                    </div>
                  </div>
                </div>

                {/* Error Message */}
                {authMessage && (
                  <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                    <IoAlertCircleSharp
                      size={20}
                      className="flex-shrink-0 mt-0.5"
                    />
                    <span>{authMessage?.slice(5).split("-").join(" ")}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3.5 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign In
                      <BsArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>

              {/* Footer */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <p className="text-gray-600 text-sm mb-4">
                    Having trouble signing in?
                  </p>
                  <div className="flex justify-center space-x-6">
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Secure Connection
                    </span>
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      SSL Encrypted
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading && <LoadingComponent />}
      <ChangePasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onSave={async (passwords) => {
          try {
            await changePassword({
              username: user?.username,
              currentPassword: passwords.currentPassword,
              newPassword: passwords.newPassword,
            });
          } catch (e) {
            // handled by changePassword
          } finally {
            setIsPasswordModalOpen(false);
          }
        }}
      />
    </div>
  );
}
